import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { AllLabels, InstallationFlowType } from '../../../constants/app.constants';
import { FormTypesData, KeyValueData } from '../../../domains/common.domain';
import { TechnicianDataService } from '../technician-data.service';
import { AllDropdownKeys } from '../../jurisdiction.constants';
import { CommonService } from '../../../services/common.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { LookupOptionsData } from '../../jurisdiction.domain';
import { EnvironmentService } from '../../../services/environment.service';
import { TechnicianDataHeatingHiddenFields } from '../../../interface/config.interface';
import { ValidationsService } from '../../../services/validations.service';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';

@Component({
  selector: 'app-heating-connection',
  styleUrls: ['./heating-connection.component.scss'],
  templateUrl: './heating-connection.component.html',
})
export class TechnicianHeatingConnectionComponent implements OnChanges, OnInit {
  @Input() currentFormType: FormTypesData;
  @Input() heatingConnectionData: any = null;
  @Input() isReadOnly: boolean = false;
  @Input() applicationId: number;
  @Input() isDetailsView: boolean;

  currentFlowType: string;
  costShareTypeKey: string;
  costShareTypes: LookupOptionsData[];
  formSubscription: Subscription;
  heatingConnectionFG: FormGroup;
  heatingLoadRequired: boolean;
  showInfoForCommodities: boolean;
  heatingSystemTypeKey: string;
  heatingSystemTypes: LookupOptionsData[];
  hiddenFields: TechnicianDataHeatingHiddenFields;
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationFlowTypes: KeyValueData;
  showConnectedLoadLabel: boolean;
  supplyTypeKey: string;
  supplyTypes: LookupOptionsData[];
  typeOfKey: string;
  typeOfValues: LookupOptionsData[];
  unitLabels: KeyValueData;
  showCompactStationNewLabel: boolean;
  sectionIdentifier = SectionIdentifier;

  constructor(
    private commonService: CommonService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private technicianDataService: TechnicianDataService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.heatingLoadRequired = this.environmentService.heatingLoadRequired;
    this.showConnectedLoadLabel = this.environmentService.showConnectedLoadLabel;
    this.showInfoForCommodities = this.environmentService.showInfoForCommodities;
    this.hiddenFields = this.environmentService.heatingHiddenFields;
    this.installationFlowTypes = InstallationFlowType;
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.heatingSystemTypeKey = AllDropdownKeys.HeatingTypeOfHeating;
    this.supplyTypeKey = AllDropdownKeys.HeatingDistributionType;
    this.typeOfKey = AllDropdownKeys.HotWaterPrepType;
    this.infoBubblesData = this.environmentService.getInfoBubblesData('technicianData') || null;

    this.costShareTypeKey = AllDropdownKeys.VariableCosts;
    this.costShareTypes = this.commonService.getDropdownData(this.costShareTypeKey);
    this.heatingSystemTypes = this.commonService.getDropdownData(this.heatingSystemTypeKey);
    this.supplyTypes = this.commonService.getDropdownData(this.supplyTypeKey);
    this.typeOfValues = this.commonService.getDropdownData(this.typeOfKey);
    this.unitLabels = AllLabels.units;
    this.prepareForm();
    this.technicianDataService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.emitFormData();
        this.resetData();
      }
    });

    this.showCompactStationNewLabel =
      this.environmentService.showCompactStationNewLabel && this.currentFlowType === this.installationFlowTypes.MainsConnection;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes && changes.currentFormType && !changes.currentFormType.firstChange) ||
      (changes && changes.baseData && !changes.baseData.firstChange)
    ) {
      this.prepareForm();
    }
  }

  get heatingConnectionHeaderTranslationKey(): string {
    if (this.currentFlowType === InstallationFlowType.MainsConnection) {
      return 'technician-data.heating-connection-title-new-mains';
    }

    return 'technician-data.heating-connection-title';
  }

  isFieldRequired(field: string) {
    const formField = this.heatingConnectionFG.get(field);
    return ValidationsService.isFieldRequired(formField);
  }

  private prepareForm(): void {
    this.heatingConnectionFG = new FormGroup({
      ...(!this.environmentService.showOnlyPeakFlow && {
        heatingLoad: new FormControl(
          (this.heatingConnectionData && this.heatingConnectionData.heatingLoad) || this.environmentService.defaultHeatingLoadValue
        ),
      }),
      ...(this.environmentService.showOnlyPeakFlow && {
        peakFlow: new FormControl((this.heatingConnectionData && this.heatingConnectionData.peakFlow) || ''),
      }),
      id: new FormControl((this.heatingConnectionData && this.heatingConnectionData.id) || ''),
    });
    if (!this.hiddenFields.dhwheating) {
      this.heatingConnectionFG.addControl(
        'dhwheating',
        new FormControl((this.heatingConnectionData && this.heatingConnectionData.dhwheating) || false, [Validators.required])
      );
    }
    if (
      !this.hiddenFields.supplyType &&
      (!this.hiddenFields.supplyTypeForNewMainsConnection || this.currentFlowType !== this.installationFlowTypes.MainsConnection)
    ) {
      this.heatingConnectionFG.addControl(
        'supplyType',
        new FormControl((this.heatingConnectionData && this.heatingConnectionData.supplyType) || '', [Validators.required])
      );
    }
    if (!this.hiddenFields.heatingSystemType) {
      this.heatingConnectionFG.addControl(
        'heatingSystemType',
        new FormControl((this.heatingConnectionData && this.heatingConnectionData.heatingSystemType) || '', [Validators.required])
      );
    }
    if (this.currentFormType.index < 2 && !this.hiddenFields.compactStation) {
      this.heatingConnectionFG.addControl(
        'compactStation',
        new FormControl((this.heatingConnectionData && this.heatingConnectionData.compactStation) || false, [Validators.required])
      );
    }

    if (this.currentFormType.index > 0 && !this.environmentService.showOnlyPeakFlow) {
      this.heatingConnectionFG.addControl(
        'singleBillCheckbox',
        new FormControl((this.heatingConnectionData && this.heatingConnectionData.singleBillCheckbox) || false, [Validators.required])
      );
      this.heatingConnectionFG.addControl(
        'typeOf',
        new FormControl((this.heatingConnectionData && this.heatingConnectionData.typeOf) || '')
      );
      if (!this.hiddenFields.variableCostsShare) {
        this.heatingConnectionFG.addControl(
          'variableCostsShare',
          new FormControl((this.heatingConnectionData && this.heatingConnectionData.variableCostsShare) || this.costShareTypes[0].value)
        );
      }
      if (!this.hiddenFields.smokeAlarmServiceCheckbox) {
        this.heatingConnectionFG.addControl(
          'smokeAlarmServiceCheckbox',
          new FormControl((this.heatingConnectionData && this.heatingConnectionData.smokeAlarmServiceCheckbox) || false, [
            Validators.required,
          ])
        );
      }
    }

    if (this.currentFlowType === this.installationFlowTypes.MainsConnection) {
      if (this.heatingLoadRequired) {
        this.heatingConnectionFG.controls.heatingLoad?.setValidators(Validators.required);
      }
    }
    this.emitFormData();

    this.formSubscription = this.heatingConnectionFG.valueChanges.subscribe(() => {
      this.emitFormData();
    });
    this.cdr.detectChanges();
  }

  private emitFormData(): void {
    this.technicianDataService.registerForComponentFormUpdate.next({
      component: 'heatingConnection',
      formData: this.heatingConnectionFG,
    });
  }

  private resetData(): void {
    this.heatingConnectionData = null;
    this.formSubscription.unsubscribe();
  }
}
